/*
 * <sd-form> — inline validation for bespoke forms (currently the contact form).
 *
 * Progressive enhancement: `novalidate` is set HERE rather than in the markup, so
 * a browser with JS off still gets native validation instead of none at all.
 *
 * Rules come from the standard HTML attributes (required / type=email / pattern /
 * minlength) — this only replaces the browser's grey bubble with an inline message
 * in the field. Copy comes from data-error-* on the control, so the wording stays
 * in Liquid (schema-editable) instead of hard-coded here.
 *
 * The <sd-select> dropdown posts through a hidden input, and hidden inputs are
 * barred from constraint validation, so `required` on one is checked by hand and
 * the message/aria are pointed at the visible <summary>.
 */
class SdForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.form.noValidate = true;
    this.form.addEventListener('submit', this.onSubmit.bind(this));
    // Re-check only fields already flagged — validating as someone first types a
    // half-finished email is the classic way to make a form feel hostile.
    this.form.addEventListener('input', this.onChange.bind(this));
    this.form.addEventListener('change', this.onChange.bind(this));
    this.addEventListener('sd-select:change', this.onChange.bind(this));
  }

  get controls() {
    return Array.from(this.form.querySelectorAll('[data-error-required], [data-error-invalid]'));
  }

  onSubmit(event) {
    const invalid = this.controls.filter((control) => !this.validate(control));
    if (!invalid.length) return;

    event.preventDefault();
    const first = invalid[0];
    this.focusTarget(first).focus({ preventScroll: true });
    this.field(first)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  onChange(event) {
    const control = event.target.closest('[data-error-required], [data-error-invalid]');
    const target = control || event.target.closest('sd-select')?.querySelector('[data-error-required]');
    if (target && this.field(target)?.classList.contains('is-invalid')) this.validate(target);
  }

  /* Returns true when valid, and paints the field either way. */
  validate(control) {
    const value = (control.value || '').trim();
    let message = '';

    if (control.hasAttribute('required') && !value) {
      message = control.dataset.errorRequired || 'This field is required.';
    } else if (value && control.willValidate && !control.checkValidity()) {
      message = control.dataset.errorInvalid || control.validationMessage;
    }

    if (message) this.showError(control, message);
    else this.clearError(control);

    return !message;
  }

  showError(control, message) {
    const field = this.field(control);
    if (!field) return;

    let el = field.querySelector('.sd-field__error');
    if (!el) {
      el = document.createElement('p');
      el.className = 'sd-field__error';
      el.id = `${control.id || control.name}-error`.replace(/[^\w-]/g, '');
      el.setAttribute('role', 'alert');
      field.appendChild(el);
    }
    el.textContent = message;
    field.classList.add('is-invalid');

    const target = this.focusTarget(control);
    target.setAttribute('aria-invalid', 'true');
    target.setAttribute('aria-describedby', el.id);
  }

  clearError(control) {
    const field = this.field(control);
    if (!field) return;

    field.classList.remove('is-invalid');
    field.querySelector('.sd-field__error')?.remove();

    const target = this.focusTarget(control);
    target.removeAttribute('aria-invalid');
    target.removeAttribute('aria-describedby');
  }

  field(control) {
    return control.closest('.sd-field');
  }

  /* Where the error and focus belong — the hidden dropdown input can hold neither. */
  focusTarget(control) {
    if (control.type !== 'hidden') return control;
    return control.closest('sd-select')?.querySelector('summary') || control;
  }
}

if (!customElements.get('sd-form')) customElements.define('sd-form', SdForm);
